import React, { ReactNode, useCallback } from 'react';

import { useRouter } from 'next/router';
import styled, { useTheme } from 'styled-components';

import { FormattedMessage } from '@techstyle/react-intl';

import { desktop, mobile } from '../../styles';
import { BasicButton } from '../../styles/button';
import { Paragraph } from '../../styles/paragraph';
import { useLDNewShoppingBagUX } from '../../utils/LD/useNewShoppingBagUX';
import { useVipCTAStyling } from '../../utils/useVipCTAStyling';
import { Component as ShopNowLink } from '../ShopNowLink';

const Wrapper = styled.div`
  margin: auto;
  text-align: center;

  > a {
    ${({ theme }) => theme.links.variants.textLinkSecondary}
    color: ${({ theme }) => theme.colors.default};
  }
`;

const EmptyCopy = styled(Paragraph).attrs({ size: '18px' })`
  margin-bottom: ${({ theme }) => theme.spacing.tiny}px;

  ${({ theme }) => theme.paragraph.variants.paragraph2Uppercase.textStyles}
`;

const NewMessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 474px;
  margin: auto;
  ${mobile`
    padding: ${({ theme }) =>
      theme.context.cartDrawer
        ? '0'
        : `0 ${theme.spacing.small}px ${theme.spacing.medium}px ${theme.spacing.small}px`};
  `}
  ${desktop`
    padding: 0 ${({ theme }) => theme.spacing.small}px ${({ theme }) =>
    theme.spacing.xlarge}px ${({ theme }) => theme.spacing.small}px;
  `}
  text-align: center;
  justify-content: center;
  height: calc(100% - ${({ theme }) => theme.spacing.xlarge}px);
`;

const CheckoutButton = styled(BasicButton).attrs({
  fullWidth: true,
})<{ children?: ReactNode; alternate: boolean; onClick: () => void }>`
  ${mobile`
    width: calc(100% - ${({ theme }) => theme.spacing.small * 2}px);
    margin-left: ${({ theme }) => theme.spacing.small}px;
  `}
`;

const EmptyCopyTitle = styled.span`
  ${({ theme }) => theme.header.variants.h2.textStyles};
  margin-bottom: ${({ theme }) => theme.spacing.tiny}px;
`;

const EmptyCopyMessage = styled.span`
  ${({ theme }) => theme.paragraph.variants.paragraph3.textStyles};
  margin-bottom: ${({ theme }) => theme.spacing.moderate}px;
`;

type EmptyCartMessageProps = {
  className: string;
};
const EmptyCartMessage = ({ className }: EmptyCartMessageProps) => {
  const shoppingBagFlagStatus = useLDNewShoppingBagUX();
  const isVipCTAStyling = useVipCTAStyling();
  const router = useRouter();

  const { context } = useTheme();

  const handleShopNow = useCallback(() => {
    router.push('/features/best-sellers');
  }, [router]);

  if (shoppingBagFlagStatus?.isControl) {
    return (
      <Wrapper className={className}>
        <EmptyCopy data-autotag="mini-cart-empty-message">
          <FormattedMessage
            id="global_checkout.shopping_bag_empty"
            defaultMessage="EMPTY :("
          />
        </EmptyCopy>
        <ShopNowLink />
      </Wrapper>
    );
  }
  return (
    <NewMessageWrapper className={className}>
      <EmptyCopyTitle data-autotag="mini-cart-empty-title">
        {context.cartDrawer ? (
          <FormattedMessage
            id="global_checkout.shopping_bag_empty_title"
            defaultMessage="Your bag is empty"
          />
        ) : (
          <FormattedMessage
            id="global_checkout.shopping_cart_empty_title"
            defaultMessage="Your cart is empty"
          />
        )}
      </EmptyCopyTitle>
      <EmptyCopyMessage data-autotag="mini-cart-empty-message">
        <FormattedMessage
          id="global_checkout.browse_our_collection"
          defaultMessage="Browse our collection and find your perfect fit!"
        />
      </EmptyCopyMessage>
      <CheckoutButton onClick={handleShopNow} alternate={isVipCTAStyling}>
        <FormattedMessage id="global_cta.shop_now" defaultMessage="Shop now" />
      </CheckoutButton>
    </NewMessageWrapper>
  );
};

export default EmptyCartMessage;
